import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL, getWsBaseUrl } from '../config';
import { useProject } from '../context/ProjectContext';
import { openAgentVscode } from '../utils/openAgentVscode';

interface AgentRun {
  run_id: string;
  agent_id: string;
  ticket_key?: string | null;
  mode: string;
  status: string;
  status_detail?: string | null;
  progress?: Record<string, string> | null;
  branch_name?: string | null;
  worktree_path?: string | null;
  pr_url?: string | null;
  created_at?: string | null;
  approval_gate?: string | null;
  pending_question?: string | null;
}

const ACTIVE = new Set(['pending', 'queued', 'running', 'waiting_user']);

const statusLabel = (s: string) => {
  if (s === 'waiting_user') return 'čeká na tebe';
  if (s === 'running') return 'běží';
  if (s === 'pending' || s === 'queued') return 've frontě';
  if (s === 'success') return 'hotovo';
  if (s === 'failed') return 'selhalo';
  if (s === 'cancelled') return 'zrušeno';
  return s;
};

const statusTone = (s: string) => {
  if (s === 'queued' || s === 'pending') return 'border-slate-400/40 bg-slate-50/80 dark:bg-slate-950/40';
  if (s === 'running') return 'border-blue-400/40 bg-blue-50/80 dark:bg-blue-950/40';
  if (s === 'waiting_user') return 'border-amber-400/50 bg-amber-50/80 dark:bg-amber-950/40';
  if (s === 'success') return 'border-emerald-400/40 bg-emerald-50/70 dark:bg-emerald-950/30';
  if (s === 'failed') return 'border-red-400/40 bg-red-50/70 dark:bg-red-950/30';
  return 'border-gray-300/50 bg-white/70 dark:bg-gray-800/50';
};

const progressDone = (progress?: Record<string, string> | null) => {
  if (!progress) return 0;
  const vals = Object.values(progress);
  if (!vals.length) return 0;
  const done = vals.filter((v) => v === 'done').length;
  return Math.round((done / vals.length) * 100);
};

const relativeTime = (iso?: string | null) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'právě teď';
  if (mins < 60) return `před ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `před ${hrs} h`;
  return `před ${Math.round(hrs / 24)} d`;
};

const ComplicDashboard: React.FC = () => {
  const { currentProject } = useProject();
  const [time, setTime] = useState(new Date());
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [liveByRun, setLiveByRun] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const refresh = useCallback(async () => {
    if (!currentProject?.id) return;
    try {
      const r = await axios.get(`${API_URL}/api/agents/runs`, {
        params: { project_id: currentProject.id, limit: 30 },
      });
      setRuns(r.data.runs || []);
    } catch (e) {
      console.error(e);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [refresh]);

  // Live WS for active runs (first few)
  useEffect(() => {
    const active = runs.filter((r) => ACTIVE.has(r.status)).slice(0, 4);
    if (!active.length) return;
    const sockets: WebSocket[] = [];
    active.forEach((run) => {
      const ws = new WebSocket(`${getWsBaseUrl()}/ws/agent-${run.run_id}`);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === 'agent_event' && data.event?.message) {
            setLiveByRun((prev) => ({ ...prev, [run.run_id]: data.event.message }));
          }
          if (data.type === 'agent_status') refresh();
        } catch {
          /* ignore */
        }
      };
      sockets.push(ws);
    });
    return () => sockets.forEach((ws) => ws.close());
  }, [runs, refresh]);

  const { active, recent } = useMemo(() => {
    const a = runs.filter((r) => ACTIVE.has(r.status));
    const finished = runs.filter((r) => !ACTIVE.has(r.status)).slice(0, 8);
    return { active: a, recent: finished };
  }, [runs]);

  const approve = async (runId: string, text?: string) => {
    try {
      setSendingId(runId);
      await axios.post(`${API_URL}/api/agents/runs/${runId}/approve`, {
        ...(text?.trim() ? { answer: text.trim() } : {}),
      });
      setAnswers((prev) => ({ ...prev, [runId]: '' }));
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setSendingId(null);
    }
  };

  const renderCard = (run: AgentRun, highlight?: boolean) => {
    const pct = progressDone(run.progress);
    const live = liveByRun[run.run_id];
    return (
      <div
        key={run.run_id}
        className={`
          rounded-2xl border px-4 py-3 shadow-soft backdrop-blur-sm
          transition-all duration-300
          ${statusTone(run.status)}
          ${highlight ? 'ring-1 ring-complic-400/30' : ''}
        `}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
                {run.agent_id}
              </span>
              {run.ticket_key && (
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10 text-gray-700 dark:text-gray-200">
                  {run.ticket_key}
                </span>
              )}
              <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {run.mode}
              </span>
            </div>
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {statusLabel(run.status)}
              {run.status_detail ? ` · ${run.status_detail}` : ''}
            </div>
            {live && (
              <div className="mt-1 text-[11px] text-blue-700 dark:text-blue-300 truncate">
                {live}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-lg font-semibold tabular-nums text-gray-800 dark:text-gray-100">
              {pct}%
            </div>
            <div className="text-[10px] text-gray-500">{relativeTime(run.created_at)}</div>
          </div>
        </div>

        {run.progress && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(run.progress).map(([k, v]) => (
              <span
                key={k}
                className={`
                  text-[10px] px-1.5 py-0.5 rounded
                  ${v === 'done' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' :
                    v === 'running' ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300' :
                    v === 'skipped' ? 'bg-gray-400/15 text-gray-500' :
                    'bg-gray-400/10 text-gray-500'}
                `}
              >
                {k}
              </span>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-center gap-3 text-xs">
          {run.pr_url && (
            <a
              href={run.pr_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              PR
            </a>
          )}
          {run.branch_name && (
            <span className="font-mono text-gray-500 truncate max-w-[12rem]">{run.branch_name}</span>
          )}
          {run.branch_name && (
            <button
              type="button"
              onClick={() => openAgentVscode(run.run_id)}
              className="px-2 py-1 rounded bg-blue-500/15 text-blue-700 dark:text-blue-300 hover:bg-blue-500/25"
              title={`Open VS Code on ${run.branch_name}`}
            >
              VS Code
            </button>
          )}
          {run.status === 'waiting_user' && !run.pending_question && (
            <button
              type="button"
              onClick={() => approve(run.run_id)}
              className="ml-auto px-2 py-1 rounded bg-amber-500/20 text-amber-800 dark:text-amber-200 hover:bg-amber-500/30"
            >
              Schválit
            </button>
          )}
        </div>

        {run.status === 'waiting_user' && run.pending_question && (
          <div className="mt-2 space-y-1.5">
            <p className="text-xs text-amber-900 dark:text-amber-100">{run.pending_question}</p>
            <div className="flex gap-2">
              <input
                value={answers[run.run_id] || ''}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, [run.run_id]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (answers[run.run_id] || '').trim()) {
                    approve(run.run_id, answers[run.run_id]);
                  }
                }}
                placeholder="Odpověď…"
                className="flex-1 text-xs px-2 py-1 rounded border border-amber-300/60 dark:border-amber-700 bg-white/80 dark:bg-gray-900/60"
              />
              <button
                type="button"
                disabled={sendingId === run.run_id || !(answers[run.run_id] || '').trim()}
                onClick={() => approve(run.run_id, answers[run.run_id])}
                className="px-2 py-1 rounded bg-amber-500/30 text-amber-900 dark:text-amber-100 text-xs disabled:opacity-40"
              >
                Odeslat
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full relative overflow-hidden">
      <div className="relative z-10 h-full flex flex-col p-6 md:p-8 max-w-6xl mx-auto">
        <header className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50">
              Dashboard
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Běžící a nedávno dokončené agent runs
              {currentProject?.name ? ` · ${currentProject.name}` : ''}
            </p>
          </div>
          <div className="text-right text-sm text-slate-500 dark:text-slate-400 tabular-nums bg-white/70 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-700 rounded-2xl px-3 py-2 shadow-soft">
            <div className="font-semibold text-slate-800 dark:text-slate-100">{time.toLocaleTimeString('cs-CZ', { hour12: false })}</div>
            <div className="text-xs">
              {time.toLocaleDateString('cs-CZ', { year: 'numeric', month: '2-digit', day: '2-digit' })}
            </div>
          </div>
        </header>

        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Běží teď
            </h2>
            <span className="text-xs tabular-nums text-gray-400">{active.length}</span>
          </div>
          {active.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-6 px-1">
              Žádný agent teď neběží. Spusť Developer z tiketu nebo přes Complic chat.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {active.map((r) => renderCard(r, true))}
            </div>
          )}
        </section>

        <section className="flex-1 min-h-0 overflow-auto">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Nedávno
            </h2>
            <span className="text-xs tabular-nums text-gray-400">{recent.length}</span>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-4 px-1">
              Zatím žádné dokončené běhy.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pb-24">
              {recent.map((r) => renderCard(r))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ComplicDashboard;
